import { computedFrom, autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { Customer } from '../../models/customer';
import { Portfolio } from '../../models/portfolio';
import { PortalUtility } from '../../helpers/utility';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { BasePage } from '../base-page';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ValidationController, ValidationControllerFactory } from 'aurelia-validation';
import { PostLoginRouteManager } from '../../helpers/post-login-route-manager';
import { VerticalMarket } from '../../models/vertical-market';

@autoinject
export class Register extends BasePage {
	validationErrors: any = [];
	i18n: any;
	isSMSEnabled: any;
	model: any;
	showTerms: any;
	showDisclosures: any;
	showPage1: any;
	showPage2: boolean;
	showReferenceId: any;
	firstName: string;
	lastName: string;
	registerError: any;
	customerAccountNumberComponent: any;
	username: string;
	accountNumber: any;
	zipCode: string;
	registrationId: string;
	registrationIdTwo: string;
	registrationIdThree: string;
	cardContainerCssClasses: string;
	isInvoiceEnabled: boolean;
	validationController: ValidationController;
	statementNotificationsPreferences: any;
	isPaperNotificationHidden: boolean;
	allowedVerticalsForStatementNotificationsSettings: Array<number> = [VerticalMarket.Pharmacy, VerticalMarket.HealthCare];

	constructor(
		private router: Router,
		private payrazrService: PayrazrService,
		private globalState: State,
		private utility: PortalUtility,
		private postLoginRouteManager: PostLoginRouteManager,
		i18n: I18N,
		eventAggregator: EventAggregator,
		validationControllerFactory: ValidationControllerFactory) {
		super(eventAggregator);
		this.isSMSEnabled = false;
		this.i18n = i18n.i18next;
		this.validationController = validationControllerFactory.createForCurrentScope();

		this.model = new Model();

		this.showTerms = false;
		this.showDisclosures = false;
		this.showPage1 = false;
		this.showPage2 = false;
		this.showReferenceId = false;
		
		this.cardContainerCssClasses = 'card w-33';

		this.isPaperNotificationHidden = false;
		this.isInvoiceEnabled = globalState.business.portalSettings.businessSettings.isInvoiceEnabled;
		this.statementNotificationsPreferences = globalState.business.portalSettings.statementNotificationsPreferences || {
			isEmailDefaultNotificationMethod: false,
			isPaperNotificationHidden: false,
			isStatementNotificationPanelHidden : false
		};
		this.handleStatementNotificationsSettings();
	}

	attached() {
		super.attached();
		this.setStartPageState();
	}

	public loadTranslations() {
		this.zipCode = this.i18n.t('zipCode', 'Zip Code');
		this.registrationId = this.i18n.t('registrationId', 'Registration ID');
		this.registrationIdTwo = this.i18n.t('registrationIdTwo', 'Registration ID 2');
		this.registrationIdThree = this.i18n.t('registrationIdThree', 'Registration ID 3');
	}
	setStartPageState() {
        var hideTerms = this.globalState.business.portalSettings.businessSettings.hideTerms
			|| this.globalState.hideSignupTerms
			|| this.globalState.business.portalSettings.businessSettings.hideTermsAck;
		var hideDisclosures = this.globalState.business.portalSettings.businessSettings.hideDisclosures;

		if (hideTerms === false) {
			this.showTerms = true;
			return;
		}

		if (hideDisclosures === false) {
			this.showDisclosures = true;
			return;
		}

		this.showPage1 = true;
	}

	defaultRegistrationFields() {
		var portalSettings = this.globalState.business.portalSettings;

		if (portalSettings.accountNumberSettings[0].required) {
			this.showReferenceId = true;
		}

		if (!this.globalState.business.portalSettings.registrationIdSettings) {
			this.globalState.business.portalSettings.registrationIdSettings = { required: false };
		}

		if (!this.globalState.business.portalSettings.registrationId2Settings) {
			this.globalState.business.portalSettings.registrationId2Settings = { required: false };
		}

		if (!this.globalState.business.portalSettings.registrationId3Settings) {
			this.globalState.business.portalSettings.registrationId3Settings = { required: false };
		}

		if (!this.globalState.business.portalSettings.zipcodeSettings) {
			this.globalState.business.portalSettings.zipcodeSettings = { required: !this.globalState.business.allowsPortalRegistrationWithoutZipCode() };
		}
	}

	@computedFrom('firstName', 'lastName')
	get customerName() {
		return `${this.firstName} ${this.lastName}`;
	}

	termsAgreed() {
		this.showTerms = false;

		if (this.globalState.business.portalSettings.businessSettings.hideDisclosures != undefined && this.globalState.business.portalSettings.businessSettings.hideDisclosures === true) {
			this.showDisclosures = false;
			this.showPage1 = true;
		}
		else {
			this.showDisclosures = true;
		}
	}

	disclosuresAgreed() {
		this.showDisclosures = false;
		this.showPage1 = true;
	}

	submit() {
		this.registerError = "";
		this.validationErrors = [];
		if (this.showPage1) {
			this.submitPage1();
		} else {
			this.submitPage2(this.isSMSEnabled);
		}
	}

	cancel() {
		this.router.navigateToRoute('login');
	}

	submitPage1() {
		let validator = new Page1Validator(this.i18n);

		if (validator.hasErrors(this.model, this.customerAccountNumberComponent, this.globalState.business.portalSettings)) {
			this.validationErrors = validator.errors;
			this.utility.scrollToTop();
		} else {
			this.payrazrService.lookupCustomerByFilters(
				this.model.accountNumber,
				this.model.registrationId,
				this.model.registrationId2,
				this.model.registrationId3,
				this.model.zipCode,
				(this.model.bucket) ? this.model.bucket.bucketKey : null
			)
				.then(response => {
					let customer = response.entity;
					if (customer == null) {
						if (response.message == 'customerAccountNotEligibleForPayments') {
							if (!this.globalState.business.portalSettings.csPhone) {
								this.validationErrors.push(this.i18n.t('customerAccountNotEligibleForPayments', { defaultValue: 'This account is not eligible for online payments.' }));
							}
							else {
								this.validationErrors.push(this.i18n.t('customerAccountNotEligibleForPaymentsWithPhoneNumber', { defaultValue: `This account is not eligible for online payments, please contact us at ${this.globalState.business.portalSettings.csPhone}.`, phoneNumber: this.globalState.business.portalSettings.csPhone }));

							}
						} 

						if (response.message == 'accountIsNoLongerActive') {
							this.validationErrors.push(this.i18n.t('accountIsNoLongerActive', { defaultValue: 'Your account is no longer active. If you feel you have received this message in error, please contact customer service.' }));
						}
						else if (!this.globalState.business.portalSettings.csPhone) {
							this.validationErrors.push(this.i18n.t('customerAccountNotFoundError', { defaultValue: 'Account cannot be located. Please contact customer service.' }));
						}
						else {
							this.validationErrors.push(this.i18n.t('customerAccountNotFoundErrorWithPhoneNumber', { defaultValue: `Account cannot be located. Please contact customer service at ${this.globalState.business.portalSettings.csPhone}`, phoneNumber: this.globalState.business.portalSettings.csPhone }));
						}

						return;
					}

					if (customer.isRegistered) {
						this.validationErrors.push(this.i18n.t('accountAlreadyRegistered', 'This account is already registered'));
					} else {
						this.model.customerId = customer.id;
						this.model.emailAddress = customer.email;
						this.model.confirmEmailAddress = customer.email;
						this.model.mobilePhone = ValidationHelpers.sanitizePhoneNumber(customer.mobilePhone);
						if ('documentSettings' in customer) {
							if ('isPaperOptedIn' in customer.documentSettings) {
								this.model.isPaperStatementsOptedIn = customer.documentSettings.isPaperOptedIn;
							}
							if ('isEmailOptedIn' in customer.documentSettings) {
								this.model.isEmailStatementsOptedIn = customer.documentSettings.isEmailOptedIn;
							}
						}
						if ('notificationSettings' in customer) {
							if ('smsIsOptedIn' in customer.notificationSettings) {
								this.model.enabledSMSNotifications = customer.notificationSettings.smsIsOptedIn;
							}
							if ('isEmailOptedIn' in customer.notificationSettings) {
								this.model.wantsEmailNotifications = customer.notificationSettings.isEmailOptedIn;
							}
						}
						let hasPortfolio = false;
						let portfolioIsConfigured = false;
						if (this.globalState.business.portalSettings.accountNumberSettings[0].required) {
							let account = customer.customerAccounts.find((element, index, array) => {
								//Property managment ONLY. They can only have 1 merchant.
								//Validate the account number part of the referenceId against user entered value. ReferenceId = [accountNumber-ownerKey]
								var index = element.referenceId.indexOf('-');
								if (index > 0 && this.globalState.business.verticalMarket == 2) {
									return element.referenceId.substring(0, index) === this.model.accountNumber;
								}
								else {
									return element.referenceId.toUpperCase() === this.model.accountNumber.toUpperCase();
								}
							});

							let portfolioName = account ? account.portfolioName : '';
							let portfolio = customer.portfolios.find(portfolio => {
								return portfolio.portfolioName == portfolioName;
							});
							hasPortfolio = !!portfolio;
							if (hasPortfolio) {
								var p = new Portfolio(portfolio);
								portfolioIsConfigured = p.hasSettings;
							}
						} else {
							customer.portfolios.forEach(portfolio => {
								var p = new Portfolio(portfolio);
								hasPortfolio = hasPortfolio || p.hasSettings;
								portfolioIsConfigured = p.hasSettings;
							});
						}

						if (hasPortfolio) {
							if (!portfolioIsConfigured) {
								if (!this.globalState.business.portalSettings.csPhone) {
									this.validationErrors.push(this.i18n.t('portalConfigurationErrorContactCustomerService', 'Portal Configuration Error: The portfolio for this account has not been setup. Please contact customer service.'));
									return;
								}
								else {
									this.validationErrors.push(this.i18n.t('portalConfigurationErrorContactCustomerServiceWithNumber', 'Portal Configuration Error: The portfolio for this account has not been setup. Please contact customer service at') + ' ' + this.globalState.business.portalSettings.csPhone + '.');
									return
								}
							}
							this.firstName = customer.firstName ? customer.firstName : "";
							this.lastName = customer.lastName ? customer.lastName : "";
							this.username = customer.email ? customer.email : "";
							this.showPage1 = false;
							this.showPage2 = true;
							this.cardContainerCssClasses = 'card card-container wide';
						} else {
							this.validationErrors.push(this.i18n.t('customerAccountNotFoundError', { defaultValue: 'Account cannot be located. Please contact customer service.' }));
						}
					}
				}).catch(response => {
					if (response.status == 404)
						this.validationErrors.push(this.i18n.t('yourAccountNoLongerActive', "Your account is no longer active. If you feel you have received this message in error, please contact customer service."));
					else {
						var baseMessage = this.i18n.t('unableToRetrieveAccountInformation',"We are unable to retrieve your account information, please try again shortly.");
						if (!this.globalState.business.portalSettings.csPhone)
							this.validationErrors.push(baseMessage);
						else
							this.validationErrors.push(baseMessage + ' ' + this.i18n.t('ifIssuePersistsPleaseContact', 'If the issue persists please contact us at') + ' ' + this.globalState.business.portalSettings.csPhone + ".");
					}
				});
		}
	}

	submitPage2(isSmsEnabled) {
		let validator = new Page2Validator(isSmsEnabled, this.i18n, this.isInvoiceEnabled);
		if (validator.hasErrors(this.model)) {
			this.validationErrors = validator.errors;
			this.utility.scrollToTop();
		} else {
			this.payrazrService.registerConsumerUser(this.model)
				.then(response => {
					if (response.isSuccessful) {
						let customer = response.entity.customer;
						this.globalState.customer = new Customer(this.globalState.business, customer);
						this.globalState.token = response.entity.token;
						let routeResponse = this.postLoginRouteManager.calculateRoute();
						if (routeResponse.routeSettings) {
							this.router.currentInstruction.config.settings = routeResponse.routeSettings;
						}
						this.router.navigateToRoute(routeResponse.route);
					}
					else {
						if (response.message === 'accountAlreadyRegisteredWithThatUsername') {
							response.message = this.i18n.t('accountAlreadyRegisteredWithThatUsername', 'An account is already registered with that username');
						}
						this.registerError = response.message;
						}
				})
				.catch(error => {
					//TODO: handle UI http requests errors
				});
		}
	}

	private shouldRedirectToPreferredLanguagePage(): boolean {
		if (this.globalState.business?.portalSettings?.businessSettings == null ||
			this.globalState.customer == null) {
			return false;
		}

		return this.globalState.business.portalSettings.businessSettings.enablePreferredLanguageAcknowledgement &&
			this.globalState.customer.preferredLanguageSettings.preferredLanguageCode == null &&
			this.globalState.customer.preferredLanguageSettings.declinedToStatePreferredLanguage == null &&
			this.globalState.customer.preferredLanguageSettings.otherPreferredLanguage == null;
	}

	//TODO: Chris add computedFrom
	get isSubmitting() {
		return this.payrazrService.awaitingResponse;
	}

	get showPage1Or2() {
		return this.showPage1 || this.showPage2;
	}

	activate() {
		this.isSMSEnabled = this.globalState.business.portalSettings.businessSettings.enableSMS;
		this.defaultRegistrationFields();
	}

	handleStatementNotificationsSettings() {
		const isBusinessVerticalAllowed = this.allowedVerticalsForStatementNotificationsSettings.some(x => x == this.globalState.business.verticalMarket);
		if (this.isInvoiceEnabled && isBusinessVerticalAllowed) {
			if (this.statementNotificationsPreferences.isStatementNotificationPanelHidden) {
				this.isInvoiceEnabled = false; // threat view as this settings it's not enabled
			} else if (this.statementNotificationsPreferences.isPaperNotificationHidden) {
				this.model.isPaperStatementsOptedIn = false;
				this.model.isEmailStatementsOptedIn = true;
				this.isPaperNotificationHidden = true;
			} else if (this.statementNotificationsPreferences.isEmailDefaultNotificationMethod) {
				this.model.isPaperStatementsOptedIn = false;
				this.model.isEmailStatementsOptedIn = true;
			}
		}
	}
}

class Model {
	customerId = '';
	accountNumber = '';
	registrationId = '';
	registrationId2 = '';
	registrationId3 = '';
	zipCode = '';
	bucketKey = null;

	username = '';
	password = '';
	confirmPassword = '';

	question1: any;
	question2: any;
	question1Answer = '';
	question2Answer = '';

	emailAddress = '';
	confirmEmailAddress = '';
	wantsEmailNotifications = true;

	mobilePhone = '';
	enabledSMSNotifications = true;

	isPaperStatementsOptedIn = true;
	isEmailStatementsOptedIn = false;
}

class Page1Validator {
	errors: any;
	i18n: any;
	constructor(i18next:any) {
		this.errors = [];
		this.i18n = i18next;
	}

	hasErrors(model, customerAccountNumberComponent, portalSettings) {

		if (portalSettings.accountNumberSettings[0].required) {
			customerAccountNumberComponent.validate(this.errors);
		}

		if (portalSettings.registrationIdSettings.required) {
			if (model.registrationId.length === 0) {
				let label = '';
				if (portalSettings.registrationIdSettings.label) label = portalSettings.registrationIdSettings.label;
				else label = this.i18n.t('registrationId', 'Registration Id');
				this.errors.push(this.i18n.t('pleaseEnterYour', { defaultValue: 'Please enter your ' + label, label: label }));
			}
		}

		if (portalSettings.registrationId2Settings.required) {
			if (model.registrationId2.length === 0) {
				let label = '';
				if (portalSettings.registrationId2Settings.label) label = portalSettings.registrationId2Settings.label;
				else label = this.i18n.t('registrationIdTwo', 'Registration Id 2');
				this.errors.push(this.i18n.t('pleaseEnterYour', { defaultValue: 'Please enter your ' + label, label: label }));
			}
		}

		if (portalSettings.registrationId3Settings.required) {
			if (model.registrationId3.length === 0) {
				let label = '';
				if (portalSettings.registrationId3Settings.label) label = portalSettings.registrationId3Settings.label;
				else label = this.i18n.t('registrationIdThree', 'Registration Id 3');
				this.errors.push(this.i18n.t('pleaseEnterYour', { defaultValue: 'Please enter your ' + label, label: label }));
			}
		}

		if (portalSettings.zipcodeSettings.required) {
			if (model.zipCode.length === 0) {
				this.errors.push(this.i18n.t('enterZipCode','Please enter your zip code'));
			} else if (!ValidationHelpers.isZipCode(model.zipCode)) {
				this.errors.push(this.i18n.t('enterValidFiveOrNineDigitZipCode', 'Please enter a valid 5 or 9 digit zip code'));
			}
		}

		return this.errors.length > 0;
	}
}

class Page2Validator {
	errors: any;
	isSmsEnabled: any;
	i18n: any;

	constructor(isSmsEnabled, i18next: any, private isInvoiceEnabled: boolean) {
		this.errors = [];
		this.isSmsEnabled = isSmsEnabled;
		this.i18n = i18next;
	}

	hasErrors(model: Model) {
		if (model.username === '') {
			this.errors.push(this.i18n.t('pleaseEnterAUsername','Please enter a username.'));
		} else if (model.username.length < 6 || model.username.length > 50) {
			this.errors.push(this.i18n.t('usernameMustBeBetweenSixAndFiftyCharacters','The username must be between 6 and 50 characters.'));
		} else if (!ValidationHelpers.isValidUsername(model.username)) {
			this.errors.push(this.i18n.t('usernameCanOnlyContainCertainCharacters',"Username can only contain letters, numbers and the following special characters: _ . @"));
		}

		if (model.password === '') {
			this.errors.push(this.i18n.t('pleaseEnterAPassword','Please enter a password.'));
		} else if (model.password.length < 8 || model.password.length > 50) {
			this.errors.push(this.i18n.t('passwordsMustBeBetweenEightAndFiftyCharacters','Passwords must be between 8 and 50 characters.'));
		} else if (!ValidationHelpers.isStrongPassword(model.password)) {
			this.errors.push(this.i18n.t('passwordDoesNotMeetStrengthRequirements','Password does not meet the minimum strength requirements.'));
		} else if (model.password !== model.confirmPassword) {
			this.errors.push(this.i18n.t('passwordsDoNotMatch','The passwords do not match.'));
		}

		if (model.question1 === model.question2) {
			this.errors.push(this.i18n.t('selectTwoDifferentSecurityQuestions','Please select 2 different security questions.'));
		} else {
			if (model.question1Answer === '') {
				this.errors.push(this.i18n.t('mustProvideAnswerToFirstSecurityQuestion','You must provide an answer to the first security question.'))
			} else if (model.question1Answer.length < 3 || model.question1Answer.length > 50) {
				this.errors.push(this.i18n.t('answerToFirstSecurityQuestionMustBeBetweenThreeAndFiftyCharacters','The answer to the first security question must be between 3 and 50 characters.'));
			}

			if (model.question2Answer === '') {
				this.errors.push(this.i18n.t('mustProvideAnswerToSecondSecurityQuestion','You must provide an answer to the second security question.'));
			} else if (model.question2Answer.length < 3 || model.question2Answer.length > 50) {
				this.errors.push(this.i18n.t('answerToSecondSecurityQuestionMustBeBetweenThreeAndFiftyCharacters','The answer to the second security question must be between 3 and 50 characters.'));
			}

			if (model.question1Answer === model.question2Answer) {
				this.errors.push(this.i18n.t('answersToSecurityQuestionsMustBeDifferent','The answers to the 2 security questions must be different.'));
			}
		}

		if (this.isSmsEnabled && model.enabledSMSNotifications) {
			if (model.mobilePhone === '') {
				this.errors.push(this.i18n.t('pleaseEnterAMobileNumber','Please enter a mobile number'));
			}
			else if (!ValidationHelpers.isPhoneNumber(model.mobilePhone)) {
				this.errors.push(this.i18n.t('enterTenDigitPhoneNumber','Please enter a 10 digit mobile phone number.'));
			}
		}

		if (model.wantsEmailNotifications) {
			if (!ValidationHelpers.isEmailAddress(model.emailAddress)) {
				this.errors.push(this.i18n.t('provideValidEmail','Please provide a valid email address'));
			}

			if (model.emailAddress !== model.confirmEmailAddress) {
				this.errors.push(this.i18n.t('emailAddressesDoNotMatch','The email addresses do not match'));
			}
		}

		if (model.wantsEmailNotifications) {
			if (!ValidationHelpers.isEmailAddress(model.emailAddress)) {
				this.errors.push(this.i18n.t('provideValidEmail', 'Please provide a valid email address'));
			}

			if (model.emailAddress !== model.confirmEmailAddress) {
				this.errors.push(this.i18n.t('emailAddressesDoNotMatch', 'The email addresses do not match'));
			}
		}

		if (this.isInvoiceEnabled) {
			if (!model.isPaperStatementsOptedIn && !model.isEmailStatementsOptedIn) {
				this.errors.push(this.i18n.t('selectStatementNotificationMethod', 'Please select at least one statement notification method.'));
			}

			if (model.isEmailStatementsOptedIn) {
				if (!ValidationHelpers.isEmailAddress(model.emailAddress)) {
					this.errors.push(this.i18n.t('provideValidEmail', 'Please provide a valid email address'));
				}

				if (model.emailAddress !== model.confirmEmailAddress) {
					this.errors.push(this.i18n.t('emailAddressesDoNotMatch', 'The email addresses do not match'));
				}
			}
		}

		return this.errors.length > 0;
	}
}
